import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import AthleteAvatar from '../components/AthleteAvatar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Pencil } from 'lucide-react';

import { buildStatsDraft } from '../components/matchperf/constants';
import ObservationTimeline from '../components/matchperf/ObservationTimeline';
import PerformanceStatistics from '../components/matchperf/PerformanceStatistics';
import { VideoEvidencePanel } from '../components/matchperf/VideoEvidencePanel';
import { MatchPhysicalGPSPanel } from '../components/matchperf/MatchGpsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const noop = () => {};

const InfoTile = ({ label, value }) => (
  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-2.5"
       data-testid={`overview-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <p className="text-xs text-gray-400">{label}</p>
    <p className="font-semibold">{value ?? '—'}</p>
  </div>
);

const LEGACY_LABELS = {
  pass_accuracy: 'Pass Accuracy (%)', dribbles_success: 'Dribbles Success', dribbles_attempts: 'Dribbles Attempts',
  first_touch: 'First Touch (1-10)', positioning: 'Positioning (1-10)', decision_making: 'Decision Making (1-10)',
  distance_covered_km: 'Distance Covered (km)', sprint_count: 'Sprints', stamina: 'Stamina (1-10)',
  focus: 'Focus (1-10)', confidence: 'Confidence (1-10)',
};

const MatchReportDetailPage = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const athleteId = sp.get('athlete');
  const requestedReturn = sp.get('returnTo');
  const returnTo = requestedReturn?.startsWith('/match-reports?')
    ? requestedReturn : `/match-reports?athlete=${athleteId || ''}`;

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [obsFilter, setObsFilter] = useState('all');

  const load = useCallback(async () => {
    if (!athleteId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/athlete-match-reports/${athleteId}/matches/${matchId}`);
      setReport(res.data);
    } catch (e) {
      if (e.response?.status === 403) toast.error('You do not have permission to view this report');
      else if (e.response?.status === 404) toast.error('Match report not found');
      else toast.error('Failed to load match report');
      setReport(null);
    } finally { setLoading(false); }
  }, [athleteId, matchId]);

  useEffect(() => { load(); }, [load]);

  const tagsById = useMemo(() => {
    const map = {};
    (report?.observations || []).forEach(o => (o.development_tags || []).forEach(t => { map[t.id] = t; }));
    return map;
  }, [report]);

  const filteredObs = useMemo(() => {
    const list = report?.observations || [];
    return obsFilter === 'all' ? list : list.filter(o => o.assessment === obsFilter);
  }, [report, obsFilter]);

  const allEvidence = useMemo(() => (report?.video_evidence || []).map(ev => ({
    ...ev,
    _obs: { assessment: ev.assessment, category: ev.category,
            development_tag_ids: (ev.development_tags || []).map(t => t.id) },
  })), [report]);

  const statsDraft = useMemo(() => buildStatsDraft(report?.performance), [report]);
  const perfForGps = useMemo(() => ({ gps: report?.gps ? { ...report.gps, gps_activity_id: report.gps.gps_activity_id } : null }), [report]);
  const developmentTags = useMemo(() => {
    const seen = new Map();
    (report?.observations || []).forEach(o => (o.development_tags || []).forEach(t => seen.set(t.id, t)));
    return Array.from(seen.values());
  }, [report]);

  if (!athleteId) {
    return (
      <Layout>
        <div className="p-8 text-center text-gray-500" data-testid="mrv2-detail-missing-athlete">
          Missing athlete context. Open this report from the Match History page.
        </div>
      </Layout>
    );
  }
  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div></div>
      </Layout>
    );
  }
  if (!report) {
    return (
      <Layout>
        <div className="p-8 text-center text-gray-500" data-testid="mrv2-detail-not-found">Report not available.</div>
      </Layout>
    );
  }

  const { match, participant, coach_evaluation: evaluation, player_reflection: reflection, legacy_assessment: legacy } = report;

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8 space-y-4" data-testid="match-report-detail-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(returnTo)} data-testid="mrv2-detail-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Match History
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate(report.links?.open_match_performance)} data-testid="open-match-performance-btn">
            <Pencil className="w-3.5 h-3.5 mr-1" /> Open Match Performance
          </Button>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <AthleteAvatar name={participant.athlete_name} size="md" showName />
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-lg font-bold">vs {match.opponent}</span>
            <Badge variant="outline">{match.match_source}</Badge>
            {match.verification_status !== 'Verified' && (
              <Badge className="bg-amber-100 text-amber-800">{match.verification_status}</Badge>
            )}
          </div>
        </div>

        <Card data-testid="mrv2-overview-fixture">
          <CardHeader className="pb-2"><CardTitle className="text-base">Match Overview</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <InfoTile label="Date" value={match.match_date} />
              <InfoTile label="Competition" value={match.competition} />
              <InfoTile label="Venue" value={match.venue} />
              <InfoTile label="Club" value={match.club_name} />
              <InfoTile label="Result" value={match.result} />
              <InfoTile label="Position" value={participant.position} />
              <InfoTile label="Minutes" value={participant.minutes_played} />
              <InfoTile label="Selected / Played" value={`${participant.selected ?? '—'} / ${participant.played ?? '—'}`} />
              <InfoTile label="Goals" value={participant.goals_scored} />
              <InfoTile label="Assists" value={participant.assists} />
              <InfoTile label="Cards" value={participant.yellow_cards == null && participant.red_cards == null
                ? '—' : `${participant.yellow_cards ?? '—'}Y / ${participant.red_cards ?? '—'}R`} />
              <InfoTile label="Rating" value={participant.rating} />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="mrv2-overview-evaluation">
          <CardHeader className="pb-2"><CardTitle className="text-base">Coach Evaluation</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            {evaluation ? (
              <>
                {evaluation.overall_rating != null && <p><span className="text-gray-400">Overall rating: </span>{evaluation.overall_rating}/10</p>}
                {evaluation.strengths && <p><span className="text-gray-400">Strengths: </span>{evaluation.strengths}</p>}
                {evaluation.areas_to_improve && <p><span className="text-gray-400">Areas to improve: </span>{evaluation.areas_to_improve}</p>}
                {evaluation.tactical_notes && <p><span className="text-gray-400">Tactical notes: </span>{evaluation.tactical_notes}</p>}
                <p className="text-xs text-gray-400">By {evaluation.coach_name}</p>
              </>
            ) : <p className="text-gray-400" data-testid="mrv2-no-eval">No coach evaluation recorded for this match.</p>}
          </CardContent>
        </Card>

        <Card data-testid="mrv2-overview-reflection">
          <CardHeader className="pb-2"><CardTitle className="text-base">Player Reflection</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            {reflection ? (
              <>
              <p><span className="text-gray-400">Self rating: </span>{reflection.self_rating}/10</p>
              {reflection.what_went_well && <p><span className="text-gray-400">Went well: </span>{reflection.what_went_well}</p>}
              {reflection.what_to_improve && <p><span className="text-gray-400">To improve: </span>{reflection.what_to_improve}</p>}
              </>
            ) : <p className="text-gray-400" data-testid="mrv2-no-reflection">No player reflection recorded for this match.</p>}
          </CardContent>
        </Card>

        <Card data-testid="mrv2-statistics">
          <CardHeader className="pb-2"><CardTitle className="text-base">Statistics</CardTitle></CardHeader>
          <CardContent>
            <PerformanceStatistics
              statsDraft={statsDraft} setStatsDraft={noop} setStatsDirty={noop} statsDirty={false}
              derived={report.derived || {}} perf={report.performance} canEdit={false}
              statsSaving={false} onSave={noop} />
          </CardContent>
        </Card>

        <section className="space-y-3" data-testid="mrv2-match-analysis">
          <h2 className="text-base font-semibold">Match Analysis</h2>
          <ObservationTimeline
            observations={report.observations || []} filtered={filteredObs}
            obsFilter={obsFilter} setObsFilter={setObsFilter} tagsById={tagsById}
            canEdit={false} canDelete={false} onEdit={noop} onDelete={noop} />
        </section>

        <Card data-testid="mrv2-development-tags">
          <CardHeader className="pb-2"><CardTitle className="text-base">Development Tags</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {developmentTags.length > 0 ? developmentTags.map(tag => (
              <Badge key={tag.id} variant="outline" data-testid={`development-tag-${tag.id}`}>
                {tag.label} · {tag.category}
              </Badge>
            )) : <p className="text-sm text-gray-400" data-testid="mrv2-no-development-tags">No development tags recorded.</p>}
          </CardContent>
        </Card>

        <VideoEvidencePanel allEvidence={allEvidence} tagsById={tagsById} />

        <MatchPhysicalGPSPanel
          perf={perfForGps} matchId={matchId} athleteId={athleteId}
          activityDate={match.match_date} canEdit={false} onSaved={noop} />

        <Card data-testid="mrv2-legacy-assessment">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                Legacy Assessment
                <Badge variant="outline" className="text-[10px]">Read-only · historical</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-gray-400 mb-3">
                Pre-canonical 1–10 / percentage assessment data. Preserved for historical reference — not blended into canonical statistics above.
              </p>
              {legacy ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Object.entries(legacy).map(([cat, values]) => values && (
                  <div key={cat} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                    <p className="text-xs font-semibold uppercase text-gray-500 mb-2">{cat}</p>
                    {Object.entries(values).map(([k, v]) => v !== null && v !== undefined && (
                      <div key={k} className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-500">{LEGACY_LABELS[k] || k}</span>
                        <span className="font-medium">{v}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div> : <p className="text-sm text-gray-400" data-testid="mrv2-no-legacy-assessment">No legacy assessment recorded.</p>}
            </CardContent>
          </Card>
      </div>
    </Layout>
  );
};

export default MatchReportDetailPage;
